"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ArrowLeft, Home, AlertTriangle, FileX, ServerCrash, Wifi, LucideIcon } from "lucide-react"
import Link from "next/link"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"

interface ErrorPageTemplateProps {
  code: string
  title: string
  description: string
  icon?: LucideIcon
  iconColor?: string
  showBackButton?: boolean
  customActions?: React.ReactNode
}

export function ErrorPageTemplate({
  code,
  title,
  description,
  icon: Icon = AlertTriangle,
  iconColor = "text-red-500",
  showBackButton = true,
  customActions
}: ErrorPageTemplateProps) {
  const handleGoBack = () => {
    if (typeof window !== 'undefined') {
      window.history.back()
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Navigation />
      
      <div className="container mx-auto px-4 py-20">
        <div className="flex items-center justify-center min-h-[calc(100vh-16rem)]">
          <Card className="w-full max-w-2xl text-center">
            <CardHeader className="pb-6">
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-24 h-24 bg-muted/20 rounded-full flex items-center justify-center">
                    <Icon className={`h-12 w-12 ${iconColor}`} />
                  </div>
                  <div className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground text-sm font-bold px-2 py-1 rounded-full min-w-[3rem]">
                    {code}
                  </div>
                </div>
              </div>
              <CardTitle className="text-3xl md:text-4xl font-bold mb-4">
                {title}
              </CardTitle>
              <CardDescription className="text-lg text-muted-foreground max-w-md mx-auto">
                {description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {showBackButton && (
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={handleGoBack}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Go Back
                  </Button>
                )}
                <Button size="lg" asChild>
                  <Link href="/" className="flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Back to Home
                  </Link>
                </Button>
              </div>
              
              {customActions && (
                <div className="border-t pt-6">
                  {customActions}
                </div>
              )}
              
              <div className="text-sm text-muted-foreground pt-4">
                <p>Need help? <Link href="/contact" className="text-blue-600 hover:underline">Contact our support team</Link></p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  )
}

// Pre-configured error templates for common scenarios
export const ErrorTemplates = {
  notFound: {
    code: "404",
    title: "Page Not Found",
    description: "The page you're looking for doesn't exist or has been moved to a different location.",
    icon: FileX,
    iconColor: "text-orange-500"
  },
  serverError: {
    code: "500",
    title: "Server Error",
    description: "Something went wrong on our servers. Our team has been notified and is working to fix this issue.",
    icon: ServerCrash,
    iconColor: "text-red-500"
  },
  forbidden: {
    code: "403",
    title: "Access Forbidden",
    description: "You don't have permission to access this resource. Please check your credentials or contact support.",
    icon: AlertTriangle,
    iconColor: "text-red-500"
  },
  networkError: {
    code: "503",
    title: "Service Unavailable",
    description: "The service is temporarily unavailable. Please try again in a few moments.",
    icon: Wifi,
    iconColor: "text-yellow-500"
  },
  generic: {
    code: "ERROR",
    title: "Something went wrong",
    description: "An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.",
    icon: AlertTriangle,
    iconColor: "text-red-500"
  }
} 